import { Component, DestroyRef, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MicrosoftAuthService } from '@services/core/authentication.service';
import { ActivatedRoute, Router, RouterOutlet, NavigationStart, NavigationEnd } from '@angular/router';
import { UserService } from '@services/core/user.service';
import { StorageService } from '@services/core/storage.service';
import { AppVariablesService } from '@services/settings/app-variables.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { HeaderComponent } from './components/core/header/header.component';
import { SidebarComponent } from './components/core/sidebar/sidebar.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '@services/core/toast.service';
import { LoadingService } from '@services/core/loading.service';
import { FullScreenLoadingComponent } from '@components/core/fullscreen-loading/fullscreen-loading.component';
import { filter } from 'rxjs/operators';
import { extractAuthCode, cleanLoggedPath, buildRetryMessage, stripCodeParam } from './app-auth.utils';
import { persistSessionState } from './app-session.utils';
import { isNavigationEvent, isNavStart, isNavEnd, inLoginCooldown } from './app-auth.utils';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, HeaderComponent, SidebarComponent, FullScreenLoadingComponent],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  @Input() code?: any
  title = 'pres.Ui';
  authMicrosoft = inject(MicrosoftAuthService)
  activatedRoute = inject(ActivatedRoute)
  userService = inject(UserService)
  location = inject(Location)
  router = inject(Router)
  storageService = inject(StorageService)
  appVariablesService = inject(AppVariablesService)
  appSettings = inject(AppSettings)
  toastService = inject(ToastService)
  loadingService = inject(LoadingService)
  private destroyRef = inject(DestroyRef)
  swalBackground
  swalTextColor
  private exchangeInFlight = false
  private authAttempts = 0
  private authBlocked = false
  private readonly maxAuthAttempts = 3
  private lastLoginAt = 0
  private readonly loginCooldownMs = 10000
  appVariables = this.appVariablesService.getAppVariables()

  ngOnInit() {
    this.watchNavigation();
    this.initAuthState();
  }

  private watchNavigation() {
    this.router.events.pipe(
      filter(isNavigationEvent),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((e) => this.handleNavEvent(e));
  }

  private handleNavEvent(event: unknown) {
    if (isNavStart(event)) return this.handleNavStart();
    if (isNavEnd(event)) return this.handleNavEnd(event as NavigationEnd);
    this.loadingService.hide();
  }

  private handleNavStart() {
    this.loadingService.show();
  }

  private handleNavEnd(event: NavigationEnd) {
    this.loadingService.hide();
    this.ensureAuth(event.urlAfterRedirects);
  }

  private ensureAuth(url: string) {
    if (this.authMicrosoft.isLoggedSignal()) return;
    this.startAuthFlow(url);
  }

  private initAuthState() {
    if (this.authMicrosoft.isLoggedSignal()) return this.handleLoggedIn();
    this.startAuthFlow(this.router.url);
  }

  private startAuthFlow(url: string) {
    if (this.authBlocked) return
    this.clearAuthState();
    const code = extractAuthCode(url)
    if (!code) return this.loginMicrosoft();
    this.exchangeCode(code)
  }

  private clearAuthState() {
    this.authMicrosoft.token.set(null)
    this.userService.resetState()
  }

  private exchangeCode(code: string) {
    if (this.exchangeInFlight) return
    this.exchangeInFlight = true
    this.getToken(code)
  }

  private registerAuthFailure(): boolean {
    this.authAttempts += 1
    return this.authAttempts >= this.maxAuthAttempts
  }

  private resetAuthAttempts() {
    this.authAttempts = 0
    this.exchangeInFlight = false
  }

  private handleAuthFailure(message: string) {
    this.exchangeInFlight = false
    if (this.registerAuthFailure()) return this.failAuth(message);
    this.loginMicrosoft()
  }

  private failAuth(message: string) {
    this.authBlocked = true
    this.storageService.clearStorage()
    this.resetLoginState();
    this.toastService.error(buildRetryMessage(message))
    this.router.navigateByUrl('/')
  }

  private resetLoginState() {
    this.authMicrosoft.isLoggedSignal.set(false)
    this.authMicrosoft.token.set(null)
    this.userService.resetState()
  }

  private handleLoggedIn() {
    this.router.navigateByUrl(cleanLoggedPath(this.location.path()))
    this.getUserData()
  }

  onAppSidebarMinifiedToggled(val): void {
    this.appSettings.appSidebarMinified = !this.appSettings.appSidebarMinified;
    this.saveMinified();
  }

  private saveMinified() {
    if (localStorage) localStorage['appSidebarMinified'] = this.appSettings.appSidebarMinified;
  }

  onAppSidebarMobileToggled(val): void {
    this.appSettings.appSidebarMobileToggled = !this.appSettings.appSidebarMobileToggled;
  }

  loginMicrosoft() {
    if (inLoginCooldown(this.lastLoginAt, this.loginCooldownMs)) return;
    this.lastLoginAt = Date.now();
    this.authMicrosoft.getTokenAPI().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => this.goLogin(res),
      error: (err) => this.logLogin(err)
    });
  }

  private goLogin(res: any) {
    window.location.href = res.url;
  }

  private logLogin(err: unknown) {
    console.error('Error al iniciar sesión:', err);
  }

  getToken(code: string) {
    this.authMicrosoft.getAccessToken(code).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => this.applyToken(res),
      error: (err) => this.failToken(err)
    })
  }

  private applyToken(res: any) {
    this.resetAuthAttempts()
    persistSessionState(this.authMicrosoft, this.storageService, res)
    this.clearCodeFromUrl()
    this.getUserData();
  }

  private clearCodeFromUrl() {
    if (!this.router.url.includes("?code")) return;
    this.router.navigateByUrl(stripCodeParam(this.router.url))
  }

  private failToken(err: unknown) {
    console.error('Error al obtener el token:', err);
    this.handleAuthFailure('No fue posible obtener el token de acceso')
  }

  getUserData() {
    this.authMicrosoft.decryptToken().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => this.applyUserData(res),
      error: () => this.handleAuthFailure('No fue posible iniciar sesión')
    })
  }

  private applyUserData(res: any) {
    try {
      this.fillUserState(res);
    } catch (error) {
      console.error('Error en el procesamiento de datos:', error);
    }
  }

  private fillUserState(res: any) {
    if (!res?.user) return;
    this.userService.processUserData(res.user.user);
    this.fillUserExtras(res);
    this.authMicrosoft.isLoggedSignal.set(true);
  }

  private fillUserExtras(res: any) {
    if (res?.user) this.assignSharing(res.user);
    this.processPhoto(res?.photo);
  }

  private processPhoto(photo: any) {
    const data = photo?.body?.data;
    if (data) this.userService.processUserPhoto(data);
  }

  private assignSharing(user: any) {
    this.userService.sharingEntities.set(user.entities);
    this.userService.sharingEntity.set(user.entity);
    this.assignIdentity(user);
  }

  private assignIdentity(user: any) {
    this.userService.sharingIdentity.set(user.identity);
    this.userService.sharingMenu.set(user.menu);
    this.userService.sharingRole.set(user.role);
  }
}
